import { Job, Project } from "../types/Types";

export const PROFILE_PIC_LINK = "headshot.jpg";
export const NAME = "Christopher Faxon";
export const JOB_TITLE = "Senior Software Engineer";
export const LOCATION = "Broad Brook, CT";
export const STATUS = "Open to interesting work";

export const INTRO = [
  "Senior Software Engineer with 4+ years of full-stack expertise in Java Spring Boot, Angular, C#, and SQL. Proven track record of owning implementations end-to-end — from requirements gathering through deployment and post-go-live support — while integrating AI-assisted development tools to accelerate delivery.",
  "Experienced across multiple database platforms and technology stacks, with a history of shipping production software independently since early career.",
];

export const RESUME_LINK = "christopher_faxon_resume.pdf";

export const EMAIL = "FaxonChristopher@gmail.com";
export const PERSONAL_SITE = "https://chrisfaxon.dev";
export const PERSONAL_SITE_LABEL = "ChrisFaxon.dev";

export const UNIV_SCHOOL = "Rensselaer Polytechnic Institute";
export const UNIV_DEGREE = "Bachelor of Science in Computer Science";
export const UNIV_MINOR = "Economics of Banking and Finance";
export const UNIV_CONCENTRATION = "Systems and Software";
export const UNIV_YEARS = "Aug 2018 - May 2022";
export const UNIV_GPA = "3.66";
export const UNIV_HONORS = "4× Dean's Honor List";
export const UNIV_LOCATION = "Troy, NY";

export const UNIV_BACKGROUND_IMAGE = "rpi.png";

export const GITHUB_LINK = "https://github.com/faxonchris/";

export const LINKEDIN_LINK =
  "https://www.linkedin.com/in/christopher-faxon/";

export const EXPERTISE_AREAS: Array<string> = [
  "Full-Stack Java Spring Boot / Angular Development",
  "SQL (Oracle, SQL Server, DB2, SQLite)",
  "AI-Augmented Software Development",
  "C# / .NET",
  "End-to-End Software Implementation Lifecycle",
  "REST APIs",
  "Git",
  "Agile / Scrum",
  "Mobile Development (Xamarin, Android, iOS)",
];

export const JOBS_ARRAY: Array<Job> = [
  {
    jobName: "Senior Software Engineer",
    companyName: "Naviam (Formerly InterPro Solutions)",
    location: "Boston, MA (Remote)",
    years: "Jan 2025 - Present",
    resumeBullets: [
      "Accelerate development velocity by integrating AI-assisted engineering tools (Claude Code) into daily workflows, consistently delivering projects in <50% of initially quoted hours.",
      "Architect and deliver full-stack Enterprise Asset Management (EAM) mobile solutions to 40+ clients across higher education, manufacturing, and oil & gas sectors.",
      "Mentor junior engineers and delegate project workstreams, providing technical guidance and code reviews to elevate team capability.",
      "Drive end-to-end implementation lifecycle including requirements gathering, solution design, development, QA testing, deployment, and post-go-live support.",
      "Engineer scalable business logic within a Java Spring Boot backend powering Angular single-page applications.",
      "Author and optimize complex SQL queries across Oracle, SQL Server, IBM DB2, and SQLite databases.",
      "Serve as primary technical point of contact for stakeholders, translating business requirements into technical solutions and resolving production issues.",
    ],
    skills: [
      "Java",
      "Spring Boot",
      "Angular",
      "TypeScript",
      "JavaScript",
      "SQL",
      "Oracle",
      "SQL Server",
      "IBM DB2",
      "SQLite",
      "REST APIs",
      "AI-assisted development",
      "Agile/Scrum",
    ],
  },
    {
      jobName: "Software Engineer",
      companyName: "Naviam (Formerly InterPro Solutions)",
      location: "Boston, MA (Remote)",
      years: "Jun 2022 - Jan 2025",
      resumeBullets: [
        "Accelerate development velocity by integrating AI-assisted engineering tools (Claude Code) into daily workflows, consistently delivering projects in <50% of initially quoted hours.",
        "Architect and deliver full-stack Enterprise Asset Management (EAM) mobile solutions to 40+ clients across higher education, manufacturing, and oil & gas sectors.",
        "Drive end-to-end implementation lifecycle including requirements gathering, solution design, development, QA testing, deployment, and post-go-live support.",
        "Engineer scalable business logic within a Java Spring Boot backend powering Angular single-page applications.",
        "Author and optimize complex SQL queries across Oracle, SQL Server, IBM DB2, and SQLite databases.",
        "Serve as primary technical point of contact for stakeholders, translating business requirements into technical solutions and resolving production issues.",
      ],
      skills: [
        "Java",
        "Spring Boot",
        "Angular",
        "TypeScript",
        "JavaScript",
        "SQL",
        "Oracle",
        "SQL Server",
        "IBM DB2",
        "SQLite",
        "REST APIs",
        "AI-assisted development",
        "Agile/Scrum",
      ],
    },
    {
      jobName: "Software Engineer Intern",
      companyName: "Broadridge Financial Solutions",
      location: "New York, NY (Remote)",
      years: "May 2021 - Aug 2021",
      resumeBullets: [
        "Delivered bug fixes and feature enhancements to Shadow Financial's back-office trading platform used by global brokerages.",
        "Worked across the full C# .NET stack resolving UI, backend, and database issues.",
        "Overhauled application logging system and built wrapper functions standardizing bond calculation calls, improving maintainability and developer experience.",
      ],
      skills: ["C# .NET", "SQL", "WPF", "Agile"],
    },
    {
      jobName: "Software Engineer Intern",
      companyName: "PrecisionCare Software",
      location: "New Paltz, NY (Remote)",
      years: "Aug 2020 - May 2021",
      resumeBullets: [
        "Independently designed and shipped three production systems in C# .NET Core: a Twilio-powered telephony integration, a HIPAA-compliant telehealth platform, and a Xamarin mobile app used by thousands of healthcare workers.",
        "Developed a cross-platform mobile app enabling outpatient visit logging, significantly improving efficiency over manual processes.",
        "Refactored legacy localization codebase to improve multilingual support and long-term maintainability.",
      ],
      skills: [
        "C# .NET Core",
        "Xamarin",
        "React.js",
        "Python Flask",
        "Mobile Development",
        "Twilio API",
      ],
    },
  ];
export const PROJECTS_ARRAY: Array<Project> = [
  {
    projectName: "Hearthmap",
    projectDesc:
      "US real estate intelligence platform unifying 15+ housing, demographic, school, environmental, and political datasets onto an interactive map",
    years: "2025 - Present",
    link: "https://hearthmap.app",
    cardBackgroundImageLink: "hearthmap.PNG",
    imageLink: "hearthmap.PNG",
    resumeBullets: [
      "Architected and shipped a full-stack geographic data platform that lets users compare any US location across housing prices, schools, crime, demographics, environmental exposure, and political lean.",
      "Built a .NET 8 ingestion engine with 15+ pluggable importers (Zillow, Redfin, Census ACS, NCES, FBI UCR, EPA EJSCREEN, county-level election results) writing into a PostgreSQL/PostGIS warehouse.",
      "Designed a data inheritance pipeline that resolves values across six geographic levels (state, county, town, tract, block group, ZCTA), using ZCTA-weighted spatial averages for upward aggregation and FIPS parent-walks for downward fallback so no tile is ever blank.",
      "Built a Next.js 14 frontend with MapLibre GL vector tiles, dynamic choropleth shading, divergent color scales for political data, and zoom-aware geographic resolution.",
      "Set up automated deployment with GitHub Actions CI/CD to a DigitalOcean droplet (Docker Compose, Postgres, Redis) and Vercel for the frontend, including a custom prod-deploy orchestrator that handles tracked SQL migrations, post-deploy ingestion, materialized view refresh, and Redis cache flush.",
      "Solved real-world data edge cases: Connecticut's 2022 transition from 8 counties to 9 Planning Regions, ZCTA-to-county spatial weighting for inventory and YoY metrics, and disambiguating paired Borough/Township MCDs in NJ election data.",
    ],
    skills: [
      "C# .NET 8",
      "PostgreSQL",
      "PostGIS",
      "Next.js 14",
      "TypeScript",
      "MapLibre GL",
      "Docker",
      "GitHub Actions",
      "ETL Pipelines",
      "Redis",
      "Vercel",
      "DigitalOcean",
    ],
  },
  {
    projectName: "DiffEx",
    projectDesc: "Ethereum Derivatives DEX",
    years: "Jan 2021 - Jan 2022",
    link: "https://diffex.dev",
    cardBackgroundImageLink: "diffex-dashboard.png",
    imageLink: "diffex-screens.png",
    resumeBullets: [
      "Fully functional financial derivatives exchange for ERC20 cryptocurrency tokens.",
      "Ethereum DApp running jointly on an Ethereum Smart Contract and a .NET C# backend utilizing a PostgreSQL database",
      "Utilized Entity Framework Core as an ORM system.",
      "Frontend built in React TypeScript, utilizing various UI component libraries",
    ],
    skills: [
      "C# .NET Core",
      "Entity Framework Core",
      "React",
      "TypeScript",
      "SQL",
      "PostgreSQL",
      "Solidity",
      "Cryptocurrency",
      "Ethereum Smart Contracts",
    ],
  },
  {
    projectName: "TutorBase",
    projectDesc:
      "Rensselaer Center for Open Source - peer-to-peer tutoring network",
    years: "Aug 2021 - May 2022",
    cardBackgroundImageLink:
      "https://github.com/DangItJason/TutorBase/raw/master/client/src/containers/LandingPage/assets/img/logo2.png",
    link: "https://github.com/DangItJason/TutorBase",
    resumeBullets: [
      "Through the Rensselaer Center for Open Source, joined a team of 5 other students to create a peer-to-peer tutoring network for RPI, connecting students with tutors to help them learn.",
      "Fullstack developer of scheduling interface and notifications system.",
      "Employed Express.js for backend development, integrating a MongoDB NoSQL database for efficient data management",
    ],
    skills: ["JavaScript", "React", "Express JS", "MongoDB"],
  },
];
